import { Hash, Layers, Lock, MessageCircle, Monitor, Sparkles, Users, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type Selection } from './studioModel';

const REGIONS: { id: Selection; label: string; icon: LucideIcon; hint: string }[] = [
  { id: 'page', label: 'Page', icon: Monitor, hint: 'Theme, accent, font & surface' },
  { id: 'community', label: 'Community', icon: Sparkles, hint: 'Name, icon & join button' },
  { id: 'channels', label: 'Channels', icon: Hash, hint: 'Channel names & icons' },
  { id: 'chat', label: 'Chat', icon: MessageCircle, hint: 'Messages & composer' },
  { id: 'members', label: 'Members', icon: Users, hint: 'Member list' },
];

interface StudioLayersProps {
  selected: Selection;
  onSelect: (sel: Selection) => void;
}

/**
 * Left rail — a navigator of the fixed wireframe's regions. Selecting one focuses
 * the inspector on it. The wireframe structure is locked (no reorder / hide), so
 * this is selection-only by design.
 */
export function StudioLayers({ selected, onSelect }: StudioLayersProps) {
  return (
    <aside className="flex w-60 flex-none flex-col border-r border-white/[0.08] bg-ink-900">
      <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.7px] text-lo">
        <Layers size={13} strokeWidth={2.4} />
        Layers
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {REGIONS.map((r) => {
          const Icon = r.icon;
          const active = selected === r.id;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onSelect(r.id)}
              className={cn(
                'mb-1 flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                active ? 'bg-accent/15' : 'hover:bg-white/5',
              )}
            >
              <Icon size={15} strokeWidth={2} className={cn('mt-0.5 flex-none', active ? 'text-accent' : 'text-mid')} />
              <span className="min-w-0">
                <span className={cn('block text-[13px] font-bold', active ? 'text-accent' : 'text-hi')}>{r.label}</span>
                <span className="block truncate text-[11px] text-lo">{r.hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 border-t border-white/[0.06] px-4 py-3 text-[11px] font-semibold text-lo">
        <Lock size={12} strokeWidth={2.2} className="flex-none" />
        Layout is locked — restyle each region
      </div>
    </aside>
  );
}
